import {
  Model,
  Factory,
  belongsTo,
  trait,
  hasMany,
  Response,
  type ModelInstance,
} from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import { rand, randBoolean, randSentence, randNumber } from '@ngneat/falso'

import type { SchemaAttributes } from '../types.js'
import {
  PaginationError,
  createErrorResponse,
  filterable,
  pageable,
} from './utils.js'
import type { MirageServer, MockServer } from './server.js'
import { pad } from '@/utils/number.js'
import type { PatientData } from '@/composables/patients.js'

export type PatientLocationData = SchemaAttributes<'PatientLocation'>

export const model = (Model as ModelDefinition<PatientLocationData>).extend({
  // tell mirage that the patientLocation model does not have an inverse relationship with itself
  parent: belongsTo('patientLocation', { inverse: null }),
  children: hasMany('patientLocation', { inverse: 'parent' }),
  primaryLocationConsultations: hasMany('consultation', {
    inverse: 'consultation',
  }),
  secondaryLocationConsultations: hasMany('consultation', {
    inverse: 'consultation',
  }),
  locationAttributes: hasMany('patientLocationAttribute'),
})

export const factory = Factory.extend<PatientLocationData>({
  name(index: number) {
    return `Patient location ${pad(index + 1, 3)}`
  },
  is_active() {
    return true
  },
  status() {
    return rand(['OCCUPIED', 'READY', 'NOT_CLEANED', 'NEEDS_STERILIZATION'])
  },
  remarks() {
    return randBoolean()
      ? randSentence({ length: randNumber({ min: 3, max: 6 }) }).join(' ')
      : null
  },
  capacity() {
    return randBoolean() ? randNumber({ min: 1, max: 5 }) : null
  },
  available_for_boarding() {
    return randBoolean()
  },
  withPrimaryConsultation: trait({
    afterCreate(location, server) {
      location.update({
        primaryLocationConsultations: [
          server.create('consultation', 'withRandomPatientHealthPlans'),
        ],
      })
    },
  }),
  withSecondaryConsultation: trait({
    afterCreate(location, server) {
      location.update({
        secondaryLocationConsultations: [
          server.create('consultation', 'withRandomPatientHealthPlans'),
        ],
      })
    },
  }),
  withChildren: trait({
    name(index: number) {
      return `Room ${index + 1}`
    },
    remarks: null,
    status: null,
    capacity: null,
    afterCreate(location: ModelInstance<typeof model>, server) {
      server.createList(
        'patientLocation',
        randNumber({ min: 1, max: 3 }),
        'withPrimaryConsultation',
        {
          parent: location,
        },
      )
    },
  }),
  withAvailableForBoarding: trait({
    available_for_boarding: true,
  }),
  withPatientLocationAttributes: trait({
    afterCreate(location, server) {
      const locationAttributes = server.createList(
        'patientLocationAttribute',
        randNumber({ min: 1, max: 10 }),
      )
      location.update({ locationAttributes })
    },
  }),
  withCapacity: trait({
    afterCreate(location) {
      location.update({ capacity: randNumber({ min: 1, max: 5 }) })
    },
  }),
})

export function routes(server: MockServer) {
  server.resource('patientLocation', { path: '/patient_locations' })

  server.get(
    '/patient_locations',
    function (this: MirageServer, schema, request) {
      const locations = schema.all('patientLocation')

      // filter first
      const filtered = filterable(locations, request, {
        patient_name: (item, value) => {
          const consultations = [
            ...item.primaryLocationConsultations.models,
            ...item.secondaryLocationConsultations.models,
          ]

          return consultations
            .flatMap((c) => c.patients.models as PatientData[])
            .filter(Boolean)
            .some((p) => p.name.toLowerCase().includes(value))
        },
        'status.in': (item, value) => item.status === value,
        'id.in': (item, value) => item.id === value,
        is_active: (item, value) => item.is_active === (value === 'true'),
        available_for_boarding: (item, value) =>
          item.available_for_boarding === (value === 'true'),
        name: (item, value) =>
          item.name.toLowerCase().includes(value.toLowerCase()),
      })

      // then paginate
      try {
        const { data, meta } = pageable(filtered, request)

        return {
          ...this.serialize(data),
          meta: {
            pagination: meta,
          },
        }
      } catch (e) {
        if (e instanceof PaginationError) {
          return new Response(404, {}, e.message)
        }

        // Fallback for unexpected errors
        return new Response(500, {}, 'Internal Server Error')
      }
    },
  )

  server.get('/patient_locations/:id', function (schema, request) {
    const id = request.params.id
    const location = schema.find('patientLocation', id)

    if (!location) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `Patient location with id ${id} not found` }],
      })
    }

    return location
  })

  server.patch('/patient_locations/:id', async function (schema, request) {
    const id = request.params.id
    const attrs = JSON.parse(request.requestBody)
    const location = schema.find('patientLocation', id)

    if (!location) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `Patient location with id ${id} not found` }],
      })
    }

    if (!attrs?.data?.attributes) {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'Missing "attributes" in request body' }],
      })
    }

    await location.update(attrs.data.attributes)
    return location
  })
}

export function seeds(server: MockServer) {
  server.create('patientLocation', 'withChildren', 'withAvailableForBoarding', {
    name: 'Parent Room 1',
  })
  server.create('patientLocation', 'withChildren', 'withAvailableForBoarding', {
    name: 'Parent Room 2',
  })
  server.createList(
    'patientLocation',
    10,
    'withPrimaryConsultation',
    'withSecondaryConsultation',
    'withAvailableForBoarding',
    'withPatientLocationAttributes',
  )
  server.createList('patientLocation', 10, 'withPatientLocationAttributes')
}
